import React, { memo } from 'react';
import { Select, SelectItem, Card, CardBody, Chip } from '@heroui/react';
import { Paperclip, Warning } from '@phosphor-icons/react';
import { useMassMailContext } from '../context/MassMailContext';
import { Contact } from '../types';
import { formatFileSize } from 'shared/utils/fileValidation';
import {
  DUMMY_PREVIEW_CONTACT_ID,
  dummyPreviewContact,
  resolvePreviewContact,
} from '../utils/previewContact';
import { resolveSenderField } from '../utils/senderOptions';
import { applyFooterSender } from '../utils/footerSender';

export const EmailPreview: React.FC = memo(() => {
  console.log('📧 EmailPreview rendering...');
  
  const { state, dispatch } = useMassMailContext();
  
  console.log('📧 EmailPreview state:', {
    selectedPreviewContactId: state.selectedPreviewContactId,
    contactsCount: state.contacts?.length || 0,
    hasEmailSettings: !!state.emailSettings,
    emailSubject: state.emailSubject?.substring(0, 50),
    emailContent: state.emailContent?.substring(0, 50)
  });

  // Voorbeeld draait standaard op de eerste échte ontvanger; de voorbeeldpersoon
  // verschijnt enkel als er nog geen geldige ontvanger geselecteerd is.
  const resolvedPreview = (() => {
    try {
      return resolvePreviewContact(state.contacts, state.selectedPreviewContactId);
    } catch (error) {
      console.error('❌ Error getting preview contact:', error);
      return {
        contact: dummyPreviewContact as Contact,
        isDummy: true,
        previewableContacts: [] as Contact[],
      };
    }
  })();

  const selectedContact = resolvedPreview.contact;
  const validContacts = resolvedPreview.previewableContacts;
  console.log('✅ Selected contact for preview:', selectedContact.name);

  // Het adres waarvan de mail effectief vertrekt (een vast adres wint). Wordt op twee
  // plaatsen gebruikt: het veld "Van" en het contactadres in de standaardfooter.
  const afzenderEmail = resolveSenderField({
    senderEmail: state.senderEmail,
    lockedSenderEmail: state.lockedSenderEmail,
    allowSenderSelection: state.allowSenderSelection,
  }).email;

  // Process email content with placeholders (enhanced version with highlighting)
  const processContent = (content: string, contact: Contact, isHtml: boolean = true): string => {
    let processed = content;
    
    console.log('🔍 Processing content:', {
      contentLength: content.length,
      contactName: contact.name,
      hasContextPlaceholders: !!state.contextPlaceholders,
      contextPlaceholders: state.contextPlaceholders
    });
    
    // Replace context placeholders first (e.g., {ticketId}, {ticketTitle})
    if (state.contextPlaceholders && Object.keys(state.contextPlaceholders).length > 0) {
      console.log('✅ Processing context placeholders...');
      Object.entries(state.contextPlaceholders).forEach(([key, value]) => {
        const placeholder = `{${key}}`;
        const regex = new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g');
        
        // Ensure value is a string
        const stringValue = String(value || '');
        
        console.log(`  Replacing ${placeholder} with "${stringValue}"`);
        
        if (isHtml) {
          if (stringValue && stringValue.trim()) {
            // Show value in an orange chip for context data
            const chipHtml = `<span class="inline-block px-2 py-1 bg-orange-500 text-white text-xs font-medium rounded-full" title="Context: ${key}">${stringValue}</span>`;
            processed = processed.replace(regex, chipHtml);
          } else {
            // Show placeholder in a clean red chip when no value
            const chipHtml = `<span class="inline-block px-2 py-1 bg-red-500 text-white text-xs font-medium rounded-full" title="Leeg context veld">${placeholder}</span>`;
            processed = processed.replace(regex, chipHtml);
          }
        } else {
          processed = processed.replace(regex, stringValue || '[Leeg]');
        }
      });
    } else {
      console.log('⚠️ No context placeholders found!');
    }
    
    // Legacy-style Dutch contact placeholders (like in legacy modal)
    const contactReplacements = {
      '{Voornaam}': contact.firstName || '',
      '{Achternaam}': contact.lastName || '',
      '{VolledigeNaam}': contact.name || '',
      '{Email}': contact.email || '',
      '{MobielNummer}': contact.phoneMobile || '',
      '{ResourceID}': contact.resourceId || '',
      // Modern placeholders (for backwards compatibility)
      '{contact.firstName}': contact.firstName || '',
      '{contact.lastName}': contact.lastName || '',
      '{contact.fullName}': contact.name || '',
      '{contact.email}': contact.email || '',
      '{contact.phone}': contact.phoneMobile || '',
      '{building.name}': contact.buildingName || '',
      '{unit.number}': contact.unitNumber || '',
      '{unit.reference}': contact.units?.[0]?.referencePlus || '',
    };

    console.log('📋 Contact replacements:', contactReplacements);
    console.log('🔍 Content before contact replacement (first 500 chars):', processed.substring(0, 500));

    // Replace contact dynamic values with clean chip-style highlighting
    Object.entries(contactReplacements).forEach(([placeholder, value]) => {
      // Escape special regex characters in the placeholder
      const escapedPlaceholder = placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(escapedPlaceholder, 'g');
      
      // Ensure value is a string and handle empty values
      const stringValue = String(value || '');
      
      // Check if placeholder exists before replacement
      const countBefore = (processed.match(regex) || []).length;
      if (countBefore > 0) {
        console.log(`  ✅ Found ${countBefore}x ${placeholder} in content, value: "${stringValue}"`);
      }
      
      if (isHtml) {
        if (stringValue && stringValue.trim()) {
          // Show ONLY the value in a clean blue chip (no placeholder in title to avoid nested replacement)
          const chipHtml = `<span class="inline-block px-2 py-1 bg-blue-500 text-white text-xs font-medium rounded-full" title="Contact: ${stringValue}">${stringValue}</span>`;
          processed = processed.replace(regex, chipHtml);
        } else {
          // Show placeholder in a clean red chip when no value
          // Use a marker to avoid including the literal placeholder text in the chip
          const chipHtml = `<span class="inline-block px-2 py-1 bg-red-500 text-white text-xs font-medium rounded-full" title="Leeg contact veld">${placeholder.replace('{', '[').replace('}', ']')}</span>`;
          processed = processed.replace(regex, chipHtml);
        }
        
        // Verify replacement happened
        const countAfter = (processed.match(regex) || []).length;
        if (countBefore > 0) {
          if (countAfter === 0) {
            console.log(`    ➡️  Successfully replaced ${countBefore}x ${placeholder}`);
          } else {
            console.log(`    ⚠️  ${placeholder} still exists ${countAfter} times after replacement (was ${countBefore})!`);
          }
        }
      } else {
        processed = processed.replace(regex, stringValue || '[Leeg]');
      }
    });

    // Replace mentioned entity fields with clean chip-style highlighting (updated to match new structure)
    state.mentionedEntities.forEach(entity => {
      // Process fields from the entity.fields data (from the special search endpoint)
      if (entity.fields && entity.uniqueId) {
        Object.entries(entity.fields).forEach(([fieldName, fieldData]: [string, any]) => {
          if (fieldData.emailEnabled) {
            const placeholder = `{${entity.uniqueId}.${fieldName}}`;
            const value = fieldData.value || '';
            const regex = new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g');
            
            if (isHtml && processed.includes(placeholder)) {
              if (value && value.trim()) {
                // Show ONLY the value in a clean green chip
                const chipHtml = `<span class="inline-block px-2 py-1 bg-green-500 text-white text-xs font-medium rounded-full" title="Entiteit: ${entity.name}">${value}</span>`;
                processed = processed.replace(regex, chipHtml);
              } else {
                // Show placeholder in a clean red chip when no value
                const chipHtml = `<span class="inline-block px-2 py-1 bg-red-500 text-white text-xs font-medium rounded-full" title="Leeg entiteit veld">${placeholder}</span>`;
                processed = processed.replace(regex, chipHtml);
              }
            } else if (!isHtml) {
              processed = processed.replace(regex, value || '[Leeg]');
            }
          }
        });
      }
      
      // Also process any dynamic fields as fallback
      entity.dynamicFields?.forEach((dynamicField: any) => {
        // dynamicField is an object with {placeholder, label, value, type}
        const placeholder = typeof dynamicField === 'string' ? dynamicField : dynamicField.placeholder;
        if (!placeholder) return;
        
        const regex = new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g');
        
        if (isHtml && processed.includes(placeholder)) {
          // Show placeholder in red chip if not already processed
          const chipHtml = `<span class="inline-block px-2 py-1 bg-red-500 text-white text-xs font-medium rounded-full" title="Entiteit veld zonder data">${placeholder}</span>`;
          processed = processed.replace(regex, chipHtml);
        } else if (!isHtml) {
          processed = processed.replace(regex, '[Geen data]');
        }
      });
    });

    // Replace parameter values with highlighting
    state.parameters.forEach(param => {
      const regex = new RegExp(param.placeholder.replace(/[{}]/g, '\\$&'), 'g');
      let parameterValue = '';
      
      if (param.type === 'global') {
        parameterValue = (state.parameterValues[param.id] as string) || '';
      } else {
        const individualValues = state.parameterValues[param.id] as { [contactId: string]: string } || {};
        parameterValue = individualValues[contact.id] || '';
      }

      if (isHtml) {
        if (parameterValue && parameterValue.trim()) {
          // Show ONLY the parameter value in a clean purple chip (no placeholder in title to avoid nested replacement)
          const chipHtml = `<span class="inline-block px-2 py-1 bg-purple-500 text-white text-xs font-medium rounded-full" title="Parameter: ${parameterValue}">${parameterValue}</span>`;
          processed = processed.replace(regex, chipHtml);
        } else {
          // Show parameter placeholder in a clean red chip when empty
          const chipHtml = `<span class="inline-block px-2 py-1 bg-red-500 text-white text-xs font-medium rounded-full" title="Ontbrekende parameter">${param.placeholder}</span>`;
          processed = processed.replace(regex, chipHtml);
        }
      } else {
        processed = processed.replace(regex, parameterValue || `[${param.name}]`);
      }
    });

    // Skip "remaining placeholders" processing to avoid nested spans
    // All known placeholders should be handled by the specific replacement logic above

    return processed;
  };

  // Safely process content with error handling
  let processedSubject = '';
  let processedContent = '';
  
  console.log('🎨 Preview processing:', {
    selectedContactId: state.selectedPreviewContactId,
    selectedContactName: selectedContact.name,
    hasContextPlaceholders: !!state.contextPlaceholders,
    contextPlaceholdersCount: Object.keys(state.contextPlaceholders || {}).length,
    contextPlaceholders: state.contextPlaceholders,
    parametersCount: state.parameters?.length || 0
  });
  
  try {
    processedSubject = processContent(state.emailSubject || '', selectedContact, false);
    processedContent = processContent(state.emailContent || '', selectedContact, true);
  } catch (error) {
    console.error('❌ Error processing preview content:', error);
    processedSubject = state.emailSubject || '';
    processedContent = state.emailContent || '';
  }

  // Keuzelijst: enkel echte ontvangers. Zonder geldige ontvanger blijft de
  // voorbeeldpersoon over, zodat de gebruiker toch een voorbeeld ziet.
  const selectOptions = validContacts.length > 0
    ? validContacts.map((contact) => ({
        id: String(contact.id),
        name: `${contact.name || 'Onbekend'} (${contact.email || 'geen e-mail'})`
      }))
    : [{ id: DUMMY_PREVIEW_CONTACT_ID, name: dummyPreviewContact.name }];


  return (
    <div className="h-full flex flex-col space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">Email Voorbeeld</h3>
        <Chip size="sm" variant="flat" color="primary">
          {validContacts.length} ontvangers
        </Chip>
      </div>

      {/* Preview Contact Selector */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Voorbeeld voor contact:
        </label>
        <Select
          selectedKeys={[String(selectedContact.id)]}
          onSelectionChange={(keys) => {
            const selected = Array.from(keys)[0] as string;
            if (selected) {
              dispatch({ 
                type: 'SET_SELECTED_PREVIEW_CONTACT_ID', 
                payload: selected 
              });
            }
          }}
          size="sm"
          items={selectOptions}
        >
          {(item) => (
            <SelectItem key={item.id} textValue={item.name}>
              {item.name}
            </SelectItem>
          )}
        </Select>
      </div>

      {/* Email Preview */}
      <Card id="email-preview-section" className="flex-1 min-h-0">
        <CardBody className="p-0 flex flex-col h-full">
          {/* Email Header */}
          <div className="p-4 bg-gray-50 border-b">
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-600">Van:</span>
                {/* Zelfde bron als de composer: een vast adres (lockedSenderEmail)
                    wint van state.senderEmail, zodat het voorbeeld nooit een
                    andere afzender toont dan wat er verstuurd wordt. */}
                <span className="text-gray-900">{afzenderEmail}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-600">Naar:</span>
                <span className="text-gray-900">{selectedContact.email}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-600">Onderwerp:</span>
                <span className="text-gray-900 font-medium">
                  {processedSubject || <em className="text-gray-400">Geen onderwerp</em>}
                </span>
              </div>
              {state.attachedFiles.length > 0 && (
                <div className="flex items-start gap-2">
                  <span className="font-medium text-gray-600 shrink-0">
                    Bijlagen ({state.attachedFiles.length}):
                  </span>
                  <ul className="min-w-0 space-y-1">
                    {state.attachedFiles.map((file) => (
                      <li key={file.id} className="flex items-center gap-1.5 text-gray-900">
                        <Paperclip size={14} className="shrink-0 text-gray-500" />
                        <span className="truncate" title={file.name}>{file.name}</span>
                        <span className="shrink-0 text-xs text-gray-500">
                          ({formatFileSize(file.size)})
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Email Body */}
          <div className="flex-1 overflow-auto">
            <div className="p-4">
              {/* Email Settings Header */}
              {state.emailSettings?.headerHtml && (
                <div 
                  className="mb-4 pb-4 border-b border-gray-200"
                  dangerouslySetInnerHTML={{ __html: String(state.emailSettings.headerHtml) }}
                />
              )}

              {/* Main Content */}
              {processedContent ? (
                <div 
                  className="prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: processedContent }}
                />
              ) : (
                <em className="text-gray-400">Geen inhoud</em>
              )}

              {/* Standaardfooter uit de instellingen. Het contactadres erin volgt de
                  afzender (zie utils/footerSender.ts): een renomail vertrekt vanaf
                  leo@yally.be en mag onderaan geen ander postvak vermelden. */}
              {state.emailSettings?.footerHtml && (
                <div
                  className="mt-4 pt-4 border-t border-gray-200 text-sm"
                  dangerouslySetInnerHTML={{
                    __html: applyFooterSender(String(state.emailSettings.footerHtml), afzenderEmail),
                  }}
                />
              )}
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Validation Messages */}
      {(!state.emailSubject.trim() || !state.emailContent.trim() || validContacts.length === 0) && (
        <div className="space-y-2">
          {!state.emailSubject.trim() && (
            <div className="flex items-center gap-2 text-sm text-amber-600">
              <Warning size={16} />
              <span>Onderwerp is vereist</span>
            </div>
          )}
          {!state.emailContent.trim() && (
            <div className="flex items-center gap-2 text-sm text-amber-600">
              <Warning size={16} />
              <span>Email inhoud is vereist</span>
            </div>
          )}
          {validContacts.length === 0 && (
            <div className="flex items-center gap-2 text-sm text-amber-600">
              <Warning size={16} />
              <span>Geen geldige contacten geselecteerd</span>
            </div>
          )}
        </div>
      )}

    </div>
  );
});
